import React, { useCallback, useEffect, useMemo, useState } from 'react'
import swal from 'sweetalert'
import Spinner from 'react-bootstrap/Spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowUpRightFromSquare,
  faBolt,
  faBriefcase,
  faBullseye,
  faCompass,
  faLayerGroup,
  faLocationDot,
  faRocket,
  faUsers
} from '@fortawesome/free-solid-svg-icons'
import { Link } from 'react-router-dom'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { collection, doc, onSnapshot, query, setDoc, Timestamp, where } from 'firebase/firestore'
import { getDownloadURL, ref as storageRef, uploadBytesResumable } from 'firebase/storage'
import { db, storage } from '../firebase'
import './dash.css'

const JOBS_API_BASE =
  process.env.REACT_APP_OPPORTUNITY_SCOUT_API_BASE ||
  'https://us-central1-ubunifu-college.cloudfunctions.net/opportunityScoutApi'

const JOB_TYPE_OPTIONS = [
  { value: '', label: 'Any type' },
  { value: 'FULLTIME', label: 'Full-time' },
  { value: 'PARTTIME', label: 'Part-time' },
  { value: 'CONTRACTOR', label: 'Contractor' },
  { value: 'INTERN', label: 'Intern' },
]

const CAREER_TABS = [
  { key: 'checklist', label: 'Readiness Checklist' },
  { key: 'jobs', label: 'Job Opportunities' },
  { key: 'curriculum', label: 'Daily Curriculum' },
]

const CAREER_CHECKLIST = [
  { key: 'cvReady', label: 'CV updated' },
  { key: 'portfolioReady', label: 'Portfolio ready' },
  { key: 'linkedinReady', label: 'LinkedIn polished' },
  { key: 'githubReady', label: 'GitHub active' },
  { key: 'mockInterviewReady', label: 'Mock interview done' },
  { key: 'caseStudyReady', label: 'Case study prepared' },
]

const VENTURE_CHECKLIST = [
  { key: 'problemValidated', label: 'Problem validated' },
  { key: 'prototypeReady', label: 'Prototype ready' },
  { key: 'pilotUsers', label: 'Pilot users onboarded' },
  { key: 'pricingReady', label: 'Pricing defined' },
  { key: 'pitchReady', label: 'Pitch deck ready' },
  { key: 'operationsReady', label: 'Operations plan ready' },
]

const CAREER_CURRICULUM = [
  {
    id: 'positioning',
    dayLabel: 'Day 1',
    title: 'Career positioning and target role clarity',
    lesson: 'A focused 20-minute class on choosing the right role, packaging your story, and avoiding scattered applications.',
    tasks: [
      'Define one target role and one backup role.',
      'Write a short positioning statement that explains what you do and the kind of work you want.',
      'List three companies or sectors you want to target first.',
    ],
    deliverable: 'A short readiness brief with your target role, sectors, and positioning statement.',
  },
  {
    id: 'cv-story',
    dayLabel: 'Day 2',
    title: 'CV rewriting and achievement framing',
    lesson: 'A 20-minute session on turning classwork, freelance work, and projects into credible achievement bullets.',
    tasks: [
      'Rewrite your top three CV bullets to be outcomes-focused.',
      'Add one proof-of-work project that supports your target role.',
      'Remove weak filler content that does not strengthen your candidacy.',
    ],
    deliverable: 'An updated CV draft ready for review and submission.',
  },
  {
    id: 'proof-of-work',
    dayLabel: 'Day 3',
    title: 'Portfolio and proof-of-work upgrade',
    lesson: 'A 20-minute class on showing evidence, not just claims, through case studies, GitHub, and portfolio structure.',
    tasks: [
      'Pick your strongest project and document the problem, process, and outcome.',
      'Add screenshots, links, or a repository that shows real execution.',
      'Tighten your portfolio headline so it matches your target role.',
    ],
    deliverable: 'One polished proof-of-work entry with a public link.',
  },
  {
    id: 'visibility',
    dayLabel: 'Day 4',
    title: 'LinkedIn, GitHub, and professional visibility',
    lesson: 'A 20-minute class on making your digital footprint support your job search before recruiters ever speak to you.',
    tasks: [
      'Refresh your LinkedIn headline and about section.',
      'Pin or surface your best GitHub or proof-of-work artifact.',
      'Align your online profiles to tell the same story as your CV.',
    ],
    deliverable: 'A cleaned-up profile stack with matching public links.',
  },
  {
    id: 'applications',
    dayLabel: 'Day 5',
    title: 'Application strategy and opportunity tracking',
    lesson: 'A 20-minute class on how to apply with intent, use the jobs feed well, and avoid low-quality spray-and-pray submissions.',
    tasks: [
      'Shortlist three jobs from the admin-managed opportunity feed.',
      'Tailor your CV or opening paragraph for one real application.',
      'Set your weekly application target and decision rules.',
    ],
    deliverable: 'A mini application tracker with priority roles and tailored notes.',
  },
  {
    id: 'interviews',
    dayLabel: 'Day 6',
    title: 'Interview stories and confidence building',
    lesson: 'A 20-minute class on answering with evidence, telling better stories, and preparing for technical or behavioural screens.',
    tasks: [
      'Write two interview stories using a clear challenge-action-result flow.',
      'Prepare one project walkthrough you can explain confidently.',
      'Identify one technical or communication gap to practise with Firefly.',
    ],
    deliverable: 'An interview prep note with stories and a practice focus.',
  },
  {
    id: 'weekly-sprint',
    dayLabel: 'Day 7',
    title: 'Weekly execution sprint with Firefly',
    lesson: 'A 20-minute wrap-up session on reviewing progress, identifying blockers, and planning the next career sprint.',
    tasks: [
      'Review which readiness items moved forward this week.',
      'Submit links or notes for the work you completed.',
      'Start a Firefly session using your current blockers and next objective.',
    ],
    deliverable: 'A weekly reflection and next-step plan for career growth.',
  },
]

const DEFAULT_CURRICULUM_ENTRY = {
  completed: false,
  submitted: false,
  evidenceLink: '',
  evidenceNotes: '',
  reflection: '',
}

const buildCurriculumProgressDefaults = () => CAREER_CURRICULUM.reduce((accumulator, item) => ({
  ...accumulator,
  [item.id]: { ...DEFAULT_CURRICULUM_ENTRY },
}), {})

const DEFAULT_CAREER_FORM = {
  targetRole: '',
  preferredLocation: 'Nairobi',
  workMode: 'Hybrid',
  weeklyApplicationGoal: '5',
  portfolioUrl: '',
  linkedinUrl: '',
  githubUrl: '',
  cvUrl: '',
  cvFileName: '',
  cvUploadedAt: null,
  readinessFocus: '',
  notes: '',
  checklist: CAREER_CHECKLIST.reduce((accumulator, item) => ({
    ...accumulator,
    [item.key]: false,
  }), {}),
  curriculumProgress: buildCurriculumProgressDefaults(),
}

const DEFAULT_VENTURE_FORM = {
  startupName: '',
  oneLiner: '',
  stage: 'Idea',
  targetCustomer: '',
  revenueModel: '',
  currentTraction: '',
  nextMilestone: '',
  launchTimeline: '',
  weeklyFocus: '',
  notes: '',
  checklist: VENTURE_CHECKLIST.reduce((accumulator, item) => ({
    ...accumulator,
    [item.key]: false,
  }), {}),
}

const DEFAULT_JOB_FILTERS = {
  search: '',
  location: '',
  jobType: '',
}

const normalizeText = (value) => String(value || '').trim()

const normalizeTimestamp = (value) => {
  if (!value) return 0
  if (typeof value?.toMillis === 'function') return value.toMillis()
  if (typeof value?.seconds === 'number') return value.seconds * 1000
  if (typeof value === 'number') return value

  const parsed = new Date(value).getTime()
  return Number.isFinite(parsed) ? parsed : 0
}

const formatDateTime = (value, fallback = 'Not synced yet') => {
  const timestamp = normalizeTimestamp(value)
  if (!timestamp) return fallback

  return new Intl.DateTimeFormat('en-KE', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(timestamp))
}

const formatShortDate = (value, fallback = 'Unknown') => {
  const timestamp = normalizeTimestamp(value)
  if (!timestamp) return fallback

  return new Intl.DateTimeFormat('en-KE', {
    dateStyle: 'medium',
  }).format(new Date(timestamp))
}

const getJobDescription = (job = null) => {
  if (!job || typeof job !== 'object') return ''
  const rawJob = job.rawJob || {}
  return [
    rawJob.description,
    rawJob.job_description,
    rawJob.jobDescription,
    rawJob.snippet,
    rawJob.summary,
  ]
    .map((value) => normalizeText(value))
    .find(Boolean) || ''
}

const parseResponseJson = async (response) => {
  const rawText = await response.text()
  let data = {}

  if (rawText) {
    try {
      data = JSON.parse(rawText)
    } catch (error) {
      data = { error: rawText }
    }
  }

  if (!response.ok) {
    throw new Error(data.error || data.message || `Request failed with status ${response.status}`)
  }

  return data
}

const fetchJobsScoutWithAuth = async (auth, path) => {
  const currentUser = auth.currentUser
  if (!currentUser) {
    throw new Error('You need to be signed in to load jobs.')
  }

  const idToken = await currentUser.getIdToken()
  const response = await fetch(`${JOBS_API_BASE}${path}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
  })

  return parseResponseJson(response)
}

const buildScore = (items = []) => {
  const safeItems = items.filter((item) => item !== null && item !== undefined)
  if (!safeItems.length) return 0

  const completed = safeItems.filter((item) => {
    if (typeof item === 'boolean') return item
    return normalizeText(item).length > 0
  }).length

  return Math.round((completed / safeItems.length) * 100)
}

const buildInsight = (score, messages) => {
  if (score >= 80) return messages.high
  if (score >= 55) return messages.mid
  return messages.low
}

const mergeChecklist = (defaults, value) => ({
  ...defaults,
  ...(value || {}),
})

const mergeCurriculumProgress = (value = {}) => CAREER_CURRICULUM.reduce((accumulator, item) => ({
  ...accumulator,
  [item.id]: {
    ...DEFAULT_CURRICULUM_ENTRY,
    ...(value?.[item.id] || {}),
  },
}), {})

const hydrateCareerForm = (value = {}, userProfile = null) => {
  const defaults = {
    ...DEFAULT_CAREER_FORM,
    targetRole: value.targetRole || getSuggestedJobKeyword(userProfile?.course),
    preferredLocation: value.preferredLocation || 'Nairobi',
    readinessFocus: value.readinessFocus || getCareerFocusSuggestion(userProfile?.course),
  }

  return {
    ...defaults,
    ...value,
    checklist: mergeChecklist(DEFAULT_CAREER_FORM.checklist, value.checklist),
    curriculumProgress: mergeCurriculumProgress(value.curriculumProgress),
  }
}

const hydrateVentureForm = (value = {}) => ({
  ...DEFAULT_VENTURE_FORM,
  ...value,
  checklist: mergeChecklist(DEFAULT_VENTURE_FORM.checklist, value.checklist),
})

function getSuggestedJobKeyword(course) {
  const normalizedCourse = normalizeText(course).toLowerCase()

  if (normalizedCourse.includes('data')) return 'data analyst'
  if (normalizedCourse.includes('design') || normalizedCourse.includes('ux')) return 'ui ux designer'
  if (normalizedCourse.includes('marketing')) return 'digital marketer'
  if (normalizedCourse.includes('product')) return 'product manager'
  if (normalizedCourse.includes('business')) return 'operations associate'
  return 'software developer'
}

function getCareerFocusSuggestion(course) {
  const normalizedCourse = normalizeText(course).toLowerCase()

  if (normalizedCourse.includes('data')) return 'Sharpen SQL, dashboard storytelling, and case study presentation.'
  if (normalizedCourse.includes('design') || normalizedCourse.includes('ux')) return 'Refine portfolio case studies and visual storytelling.'
  if (normalizedCourse.includes('marketing')) return 'Package campaign results and channel strategy into a stronger profile.'
  return 'Package your best projects, interview stories, and proof of execution.'
}

function NorthstarHubComp({ initialTrack = 'career' }) {
  const auth = getAuth()
  const [activeTrack, setActiveTrack] = useState(initialTrack === 'venture' ? 'venture' : 'career')
  const [activeCareerTab, setActiveCareerTab] = useState('checklist')
  const [selectedCurriculumId, setSelectedCurriculumId] = useState(CAREER_CURRICULUM[0].id)
  const [selectedCvFile, setSelectedCvFile] = useState(null)
  const [isUploadingCv, setIsUploadingCv] = useState(false)
  const [cvUploadProgress, setCvUploadProgress] = useState(0)
  const [userProfile, setUserProfile] = useState(null)
  const [careerForm, setCareerForm] = useState(DEFAULT_CAREER_FORM)
  const [ventureForm, setVentureForm] = useState(DEFAULT_VENTURE_FORM)
  const [hubRecord, setHubRecord] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [savingTrack, setSavingTrack] = useState('')
  const [jobsLoading, setJobsLoading] = useState(false)
  const [jobsError, setJobsError] = useState('')
  const [jobs, setJobs] = useState([])
  const [latestRun, setLatestRun] = useState(null)
  const [jobFilters, setJobFilters] = useState(DEFAULT_JOB_FILTERS)

  useEffect(() => {
    setActiveTrack(initialTrack === 'venture' ? 'venture' : 'career')
  }, [initialTrack])

  useEffect(() => {
    let userUnsubscribe = null
    let hubUnsubscribe = null

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setUserProfile(null)
        setHubRecord(null)
        setCareerForm(DEFAULT_CAREER_FORM)
        setVentureForm(DEFAULT_VENTURE_FORM)
        setIsLoading(false)
        return
      }

      setIsLoading(true)

      let gotUser = false
      let gotHub = false

      const finishLoad = () => {
        if (gotUser && gotHub) {
          setIsLoading(false)
        }
      }

      userUnsubscribe = onSnapshot(
        query(collection(db, 'users'), where('userId', '==', user.uid)),
        (snapshot) => {
          gotUser = true
          const nextUserProfile = snapshot.docs[0]?.data() || null
          setUserProfile(nextUserProfile)
          finishLoad()
        },
        (error) => {
          console.error('Failed to load Northstar user profile:', error)
          gotUser = true
          setIsLoading(false)
        }
      )

      hubUnsubscribe = onSnapshot(
        doc(db, 'studentNorthstarHub', user.uid),
        (snapshot) => {
          gotHub = true
          const nextHubRecord = snapshot.exists() ? snapshot.data() : null
          setHubRecord(nextHubRecord)
          finishLoad()
        },
        (error) => {
          console.error('Failed to load Northstar hub record:', error)
          gotHub = true
          setIsLoading(false)
        }
      )
    })

    return () => {
      unsubscribe()
      if (userUnsubscribe) userUnsubscribe()
      if (hubUnsubscribe) hubUnsubscribe()
    }
  }, [auth])

  useEffect(() => {
    setCareerForm(hydrateCareerForm(hubRecord?.career, userProfile))
    setVentureForm(hydrateVentureForm(hubRecord?.venture))
  }, [hubRecord, userProfile])

  useEffect(() => {
    if (jobFilters.search) return

    const nextKeyword = careerForm.targetRole || getSuggestedJobKeyword(userProfile?.course)
    if (!nextKeyword) return

    setJobFilters((current) => ({
      ...current,
      search: current.search || nextKeyword,
    }))
  }, [careerForm.targetRole, jobFilters.search, userProfile])

  const loadJobs = useCallback(async () => {
    if (!auth.currentUser) return

    setJobsLoading(true)
    setJobsError('')

    try {
      const [jobsData, runsData] = await Promise.all([
        fetchJobsScoutWithAuth(auth, '/jobs?limit=60'),
        fetchJobsScoutWithAuth(auth, '/jobs-runs'),
      ])

      setJobs(Array.isArray(jobsData.jobs) ? jobsData.jobs : [])
      setLatestRun(Array.isArray(runsData.runs) ? runsData.runs[0] || null : null)
    } catch (error) {
      console.error('Failed to load student jobs feed:', error)
      setJobs([])
      setLatestRun(null)
      setJobsError(error.message || 'We could not load jobs right now.')
    } finally {
      setJobsLoading(false)
    }
  }, [auth])

  useEffect(() => {
    if (!userProfile || !auth.currentUser) return
    loadJobs()
  }, [auth, loadJobs, userProfile])

  const foundationScore = useMemo(() => buildScore([
    careerForm.targetRole,
    careerForm.preferredLocation,
    careerForm.workMode,
    careerForm.weeklyApplicationGoal,
    careerForm.cvUrl,
    careerForm.portfolioUrl,
    careerForm.linkedinUrl,
    careerForm.githubUrl,
  ]), [careerForm])

  const checklistScore = useMemo(() => buildScore(Object.values(careerForm.checklist)), [careerForm.checklist])

  const curriculumCompletedCount = useMemo(
    () => CAREER_CURRICULUM.filter((item) => careerForm.curriculumProgress[item.id]?.completed).length,
    [careerForm.curriculumProgress]
  )

  const curriculumSubmittedCount = useMemo(
    () => CAREER_CURRICULUM.filter((item) => careerForm.curriculumProgress[item.id]?.submitted).length,
    [careerForm.curriculumProgress]
  )

  const curriculumScore = useMemo(
    () => buildScore(CAREER_CURRICULUM.map((item) => careerForm.curriculumProgress[item.id]?.completed)),
    [careerForm.curriculumProgress]
  )

  const submissionScore = useMemo(
    () => buildScore(CAREER_CURRICULUM.map((item) => careerForm.curriculumProgress[item.id]?.submitted)),
    [careerForm.curriculumProgress]
  )

  const careerScore = useMemo(
    () => Math.round((foundationScore * 0.35) + (checklistScore * 0.25) + (curriculumScore * 0.25) + (submissionScore * 0.15)),
    [foundationScore, checklistScore, curriculumScore, submissionScore]
  )

  const ventureScore = useMemo(() => buildScore([
    ventureForm.startupName,
    ventureForm.oneLiner,
    ventureForm.stage,
    ventureForm.targetCustomer,
    ventureForm.revenueModel,
    ventureForm.currentTraction,
    ventureForm.nextMilestone,
    ventureForm.launchTimeline,
    ventureForm.weeklyFocus,
    ...Object.values(ventureForm.checklist),
  ]), [ventureForm])

  const visibleJobs = useMemo(() => {
    const searchTerm = normalizeText(jobFilters.search).toLowerCase()
    const locationTerm = normalizeText(jobFilters.location).toLowerCase()
    const typeTerm = normalizeText(jobFilters.jobType).toLowerCase().replace(/[^a-z]/g, '')

    return jobs.filter((job) => {
      const description = getJobDescription(job)
      const normalizedJobType = normalizeText(job.jobType).toLowerCase().replace(/[^a-z]/g, '')
      const searchable = [
        job.jobTitle,
        job.companyName,
        job.location,
        job.searchKeyword,
        job.sourceDomain,
        description,
      ].join(' ').toLowerCase()

      const matchesSearch = !searchTerm || searchable.includes(searchTerm)
      const matchesLocation = !locationTerm || normalizeText(job.location).toLowerCase().includes(locationTerm)
      const matchesType = !typeTerm || normalizedJobType.includes(typeTerm)

      return matchesSearch && matchesLocation && matchesType
    }).slice(0, 9)
  }, [jobFilters, jobs])

  const careerInsight = useMemo(() => buildInsight(careerScore, {
    high: 'You have a strong career engine now. Keep applying with intention and let Firefly sharpen your interview and execution gaps.',
    mid: 'You are building momentum. Tightening your proof of work and daily curriculum follow-through will move your score quickly.',
    low: 'The engine is still taking shape. Start with your CV, profile links, and the first curriculum tasks so the rest of the system has something solid to build on.',
  }), [careerScore])

  const ventureInsight = useMemo(() => buildInsight(ventureScore, {
    high: 'Your venture has strong launch momentum. Stay close to users and keep validating what converts.',
    mid: 'The concept is taking shape. Now make the offer clearer and shorten the path to real traction.',
    low: 'You have the seed of something good. Focus first on the problem, the user, and a simple first version.',
  }), [ventureScore])

  const nextFocusLabel = careerScore >= ventureScore
    ? careerForm.readinessFocus || 'Strengthen your job-readiness stack this week.'
    : ventureForm.weeklyFocus || 'Push the next startup milestone this week.'

  const missingCareerChecklist = useMemo(
    () => CAREER_CHECKLIST.filter((item) => !careerForm.checklist[item.key]).map((item) => item.label),
    [careerForm.checklist]
  )

  const readinessPriority = useMemo(() => {
    if (!careerForm.cvUrl) return 'Upload a current CV so the career engine has a real foundation to work from.'
    if (!careerForm.portfolioUrl) return 'Add a portfolio link so applications are backed by visible proof of work.'
    if (!careerForm.linkedinUrl || !careerForm.githubUrl) return 'Complete your professional link stack so your profile is recruiter-ready.'
    if (missingCareerChecklist.length) return `Close the next readiness gaps: ${missingCareerChecklist.slice(0, 2).join(' and ')}.`
    if (curriculumCompletedCount < 3) return 'Start the first three curriculum classes so Firefly has a stronger execution trail to coach from.'
    return 'Keep running the daily curriculum and turn each class into submitted evidence.'
  }, [
    careerForm.cvUrl,
    careerForm.portfolioUrl,
    careerForm.linkedinUrl,
    careerForm.githubUrl,
    curriculumCompletedCount,
    missingCareerChecklist,
  ])

  const firstIncompleteCurriculumId = useMemo(
    () => CAREER_CURRICULUM.find((item) => !careerForm.curriculumProgress[item.id]?.completed)?.id || CAREER_CURRICULUM[0].id,
    [careerForm.curriculumProgress]
  )

  useEffect(() => {
    const hasSelected = CAREER_CURRICULUM.some((item) => item.id === selectedCurriculumId)
    if (!hasSelected) {
      setSelectedCurriculumId(firstIncompleteCurriculumId)
    }
  }, [firstIncompleteCurriculumId, selectedCurriculumId])

  const selectedCurriculum = useMemo(
    () => CAREER_CURRICULUM.find((item) => item.id === selectedCurriculumId) || CAREER_CURRICULUM[0],
    [selectedCurriculumId]
  )

  const selectedCurriculumProgress = careerForm.curriculumProgress[selectedCurriculum.id] || DEFAULT_CURRICULUM_ENTRY

  const fireflyCareerPrompt = useMemo(() => {
    const role = careerForm.targetRole || 'my target role'
    return `Help me prepare for ${role}. My career readiness score is ${careerScore}%. My current priority is: ${readinessPriority} For today's coaching, focus on ${selectedCurriculum.title}. Guide me through the task and help me produce a strong submission.`
  }, [careerForm.targetRole, careerScore, readinessPriority, selectedCurriculum.title])

  const saveHub = async (track) => {
    const currentUser = auth.currentUser
    if (!currentUser) {
      swal('Sign in required', 'Please sign in again to save your Northstar Hub updates.', 'warning')
      return
    }

    setSavingTrack(track)

    try {
      const payload = {
        userId: currentUser.uid,
        studentName: userProfile?.bio || '',
        course: userProfile?.course || '',
        cohort: userProfile?.cohort || '',
        career: careerForm,
        venture: ventureForm,
        careerScore,
        ventureScore,
        updatedAt: Timestamp.now(),
      }

      if (!hubRecord?.createdAt) {
        payload.createdAt = Timestamp.now()
      }

      await setDoc(doc(db, 'studentNorthstarHub', currentUser.uid), payload, { merge: true })
      swal(
        'Saved',
        track === 'career'
          ? 'Your Career Engine has been updated.'
          : 'Your venture workspace has been updated.',
        'success'
      )
    } catch (error) {
      console.error('Failed to save Northstar Hub:', error)
      swal('Save failed', error.message || 'We could not save your hub updates right now.', 'error')
    } finally {
      setSavingTrack('')
    }
  }

  const handleCareerField = (event) => {
    const { name, value } = event.target
    setCareerForm((current) => ({ ...current, [name]: value }))
  }

  const handleVentureField = (event) => {
    const { name, value } = event.target
    setVentureForm((current) => ({ ...current, [name]: value }))
  }

  const toggleCareerCheck = (key) => {
    setCareerForm((current) => ({
      ...current,
      checklist: {
        ...current.checklist,
        [key]: !current.checklist[key],
      },
    }))
  }

  const toggleVentureCheck = (key) => {
    setVentureForm((current) => ({
      ...current,
      checklist: {
        ...current.checklist,
        [key]: !current.checklist[key],
      },
    }))
  }

  const toggleCurriculumFlag = (curriculumId, key) => {
    setCareerForm((current) => ({
      ...current,
      curriculumProgress: {
        ...current.curriculumProgress,
        [curriculumId]: {
          ...DEFAULT_CURRICULUM_ENTRY,
          ...(current.curriculumProgress[curriculumId] || {}),
          [key]: !current.curriculumProgress[curriculumId]?.[key],
        },
      },
    }))
  }

  const handleCurriculumField = (curriculumId, key, value) => {
    setCareerForm((current) => ({
      ...current,
      curriculumProgress: {
        ...current.curriculumProgress,
        [curriculumId]: {
          ...DEFAULT_CURRICULUM_ENTRY,
          ...(current.curriculumProgress[curriculumId] || {}),
          [key]: value,
        },
      },
    }))
  }

  const handleCvUpload = () => {
    const currentUser = auth.currentUser

    if (!currentUser) {
      swal('Sign in required', 'Please sign in again before uploading your CV.', 'warning')
      return
    }

    if (!selectedCvFile) {
      swal('Select a CV', 'Choose your CV file first before uploading.', 'warning')
      return
    }

    if (!storage) {
      swal('Storage error', 'Unable to connect to storage right now. Please refresh and try again.', 'error')
      return
    }

    const safeFileName = selectedCvFile.name.replace(/\s+/g, '_')
    const cvRef = storageRef(storage, `career-engine/cvs/${currentUser.uid}/${Date.now()}_${safeFileName}`)
    const uploadTask = uploadBytesResumable(cvRef, selectedCvFile)

    setIsUploadingCv(true)
    setCvUploadProgress(0)

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
        setCvUploadProgress(progress)
      },
      (error) => {
        console.error('CV upload failed:', error)
        setIsUploadingCv(false)
        swal('Upload failed', error.message || 'We could not upload your CV right now.', 'error')
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)

          setCareerForm((current) => ({
            ...current,
            cvUrl: downloadURL,
            cvFileName: selectedCvFile.name,
            cvUploadedAt: Date.now(),
            checklist: {
              ...current.checklist,
              cvReady: true,
            },
          }))

          setSelectedCvFile(null)
          setCvUploadProgress(100)
          swal('CV uploaded', 'Your CV is now attached to the Career Engine.', 'success')
        } catch (error) {
          console.error('Failed to resolve CV URL:', error)
          swal('Upload complete, but link failed', 'The CV uploaded, but we could not attach the file link.', 'warning')
        } finally {
          setIsUploadingCv(false)
        }
      }
    )
  }

  const handleFireflyComingSoon = () => {
    swal('Firefly coming soon', 'We are cooking something special. Firefly will be available here soon.', 'info')
  }

  if (isLoading) {
    return (
      <div className='dashMain northstarPage'>
        <div className='northstarShell'>
          <div className='northstarStateCard'>
            <h2>Loading Career & Startup Hub...</h2>
            <p>We are preparing your career and startup workspace.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='dashMain northstarPage'>
      <div className='northstarShell'>
        <section className='northstarHero'>
          <div className='northstarHeroCopy'>
            <p className='northstarEyebrow'>Career & Startup Hub</p>
            <h1>Manage your career readiness and startup journey in one place.</h1>
            <p>
              Choose one path at a time. Track how ready you are for work opportunities, or organise your startup idea,
              traction, and next milestone in a simple workspace.
            </p>
          </div>
          <div className='northstarHeroActions'>
            <button
              type='button'
              className={`northstarTabBtn ${activeTrack === 'career' ? 'is-active' : ''}`}
              onClick={() => setActiveTrack('career')}
            >
              <FontAwesomeIcon icon={faBriefcase} />
              <span>Career Engine</span>
            </button>
            <button
              type='button'
              className={`northstarTabBtn ${activeTrack === 'venture' ? 'is-active' : ''}`}
              onClick={() => setActiveTrack('venture')}
            >
              <FontAwesomeIcon icon={faRocket} />
              <span>Startup Hub</span>
            </button>
          </div>
        </section>

        <section className='northstarSummaryGrid'>
          <article className='northstarSummaryCard'>
            <div className='northstarSummaryIcon northstarSummaryIcon--career'>
              <FontAwesomeIcon icon={faCompass} />
            </div>
            <div>
              <span>Career readiness</span>
              <strong>{careerScore}%</strong>
            </div>
          </article>
          <article className='northstarSummaryCard'>
            <div className='northstarSummaryIcon northstarSummaryIcon--venture'>
              <FontAwesomeIcon icon={faRocket} />
            </div>
            <div>
              <span>Venture launch</span>
              <strong>{ventureScore}%</strong>
            </div>
          </article>
          <article className='northstarSummaryCard'>
            <div className='northstarSummaryIcon northstarSummaryIcon--jobs'>
              <FontAwesomeIcon icon={faLayerGroup} />
            </div>
            <div>
              <span>Jobs visible</span>
              <strong>{jobs.length}</strong>
            </div>
          </article>
          <article className='northstarSummaryCard'>
            <div className='northstarSummaryIcon northstarSummaryIcon--focus'>
              <FontAwesomeIcon icon={faBullseye} />
            </div>
            <div>
              <span>Current focus</span>
              <strong>{nextFocusLabel}</strong>
            </div>
          </article>
        </section>

        {activeTrack === 'career' ? (
          <>
            <section className='northstarPanelGrid'>
              <article className='northstarCard northstarCardMain'>
                <div className='northstarCardHeader'>
                  <div>
                    <p className='northstarSectionLabel'>Career Engine</p>
                    <h2>Build your career readiness system</h2>
                    <p className='northstarBodyCopy'>
                      This dashboard combines your readiness score, CV, proof of work, jobs feed, and Firefly-driven daily curriculum into one focused workflow.
                    </p>
                  </div>
                  <button
                    type='button'
                    className='northstarPrimaryBtn'
                    onClick={() => saveHub('career')}
                    disabled={savingTrack === 'career'}
                  >
                    {savingTrack === 'career' ? <Spinner animation='border' size='sm' /> : null}
                    <span>{savingTrack === 'career' ? 'Saving...' : 'Save Career Engine'}</span>
                  </button>
                </div>

                <div className='northstarEngineStatGrid'>
                  <article className='northstarEngineStatCard'>
                    <span>Readiness score</span>
                    <strong>{careerScore}%</strong>
                    <p>Combined from profile quality, checklist completion, curriculum progress, and submitted evidence.</p>
                  </article>
                  <article className='northstarEngineStatCard'>
                    <span>Foundations</span>
                    <strong>{foundationScore}%</strong>
                    <p>CV, target role, portfolio, work links, and application direction.</p>
                  </article>
                  <article className='northstarEngineStatCard'>
                    <span>Checklist</span>
                    <strong>{checklistScore}%</strong>
                    <p>{missingCareerChecklist.length ? `${missingCareerChecklist.length} items still need attention.` : 'All core readiness checks are in place.'}</p>
                  </article>
                  <article className='northstarEngineStatCard'>
                    <span>Curriculum</span>
                    <strong>{curriculumCompletedCount}/{CAREER_CURRICULUM.length}</strong>
                    <p>{curriculumSubmittedCount} submissions logged for Firefly follow-up.</p>
                  </article>
                </div>

                <div className='northstarFieldGrid'>
                  <label className='northstarField'>
                    <span>Target role</span>
                    <input name='targetRole' value={careerForm.targetRole} onChange={handleCareerField} placeholder='Software Developer' />
                  </label>
                  <label className='northstarField'>
                    <span>Preferred location</span>
                    <input name='preferredLocation' value={careerForm.preferredLocation} onChange={handleCareerField} placeholder='Nairobi or Remote' />
                  </label>
                  <label className='northstarField'>
                    <span>Work mode</span>
                    <select name='workMode' value={careerForm.workMode} onChange={handleCareerField}>
                      <option>Hybrid</option>
                      <option>Remote</option>
                      <option>On-site</option>
                      <option>Flexible</option>
                    </select>
                  </label>
                  <label className='northstarField'>
                    <span>Weekly application goal</span>
                    <input name='weeklyApplicationGoal' value={careerForm.weeklyApplicationGoal} onChange={handleCareerField} placeholder='5' />
                  </label>
                  <div className='northstarUploadPanel northstarField--wide'>
                    <div className='northstarUploadPanelTop'>
                      <div>
                        <span className='northstarUploadLabel'>Upload your CV</span>
                        <p className='northstarBodyCopy'>
                          Add a current CV so your readiness score and Firefly coaching are grounded in a real application asset.
                        </p>
                      </div>
                      {careerForm.cvUrl ? (
                        <a href={careerForm.cvUrl} target='_blank' rel='noreferrer' className='northstarInlineLink'>
                          Open current CV <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                        </a>
                      ) : null}
                    </div>
                    <div className='northstarUploadControls'>
                      <input
                        type='file'
                        accept='.pdf,.doc,.docx'
                        onChange={(event) => setSelectedCvFile(event.target.files?.[0] || null)}
                      />
                      <button type='button' className='northstarSecondaryBtn' onClick={handleCvUpload} disabled={isUploadingCv}>
                        {isUploadingCv ? <Spinner animation='border' size='sm' /> : <FontAwesomeIcon icon={faBolt} />}
                        <span>{isUploadingCv ? `Uploading ${cvUploadProgress}%` : 'Upload CV'}</span>
                      </button>
                    </div>
                    <div className='northstarUploadMeta'>
                      <span>{selectedCvFile ? `Selected: ${selectedCvFile.name}` : careerForm.cvFileName || 'No CV uploaded yet'}</span>
                      <strong>{careerForm.cvUploadedAt ? `Last uploaded ${formatShortDate(careerForm.cvUploadedAt)}` : 'Waiting for upload'}</strong>
                    </div>
                  </div>
                  <label className='northstarField'>
                    <span>Portfolio URL</span>
                    <input name='portfolioUrl' value={careerForm.portfolioUrl} onChange={handleCareerField} placeholder='https://portfolio...' />
                  </label>
                  <label className='northstarField'>
                    <span>LinkedIn URL</span>
                    <input name='linkedinUrl' value={careerForm.linkedinUrl} onChange={handleCareerField} placeholder='https://linkedin.com/in/...' />
                  </label>
                  <label className='northstarField northstarField--wide'>
                    <span>GitHub or proof-of-work URL</span>
                    <input name='githubUrl' value={careerForm.githubUrl} onChange={handleCareerField} placeholder='https://github.com/...' />
                  </label>
                  <label className='northstarField northstarField--wide'>
                    <span>Current readiness focus</span>
                    <textarea name='readinessFocus' value={careerForm.readinessFocus} onChange={handleCareerField} rows='3' placeholder='What are you improving this week?' />
                  </label>
                  <label className='northstarField northstarField--wide'>
                    <span>Career notes</span>
                    <textarea name='notes' value={careerForm.notes} onChange={handleCareerField} rows='4' placeholder='Interview wins, portfolio improvements, outreach ideas...' />
                  </label>
                </div>
              </article>

              <aside className='northstarSidebar'>
                <article className='northstarCard'>
                  <p className='northstarSectionLabel'>Career signal</p>
                  <h3>{careerScore}% ready</h3>
                  <p className='northstarBodyCopy'>{careerInsight}</p>
                  <div className='northstarMiniList'>
                    <div>
                      <span>Priority now</span>
                      <strong>{readinessPriority}</strong>
                    </div>
                    <div>
                      <span>Weekly applications</span>
                      <strong>{careerForm.weeklyApplicationGoal || '0'}</strong>
                    </div>
                    <div>
                      <span>Curriculum progress</span>
                      <strong>{curriculumCompletedCount} classes complete</strong>
                    </div>
                  </div>
                </article>

                <article className='northstarCard northstarCardSoft'>
                  <p className='northstarSectionLabel'>Firefly coaching</p>
                  <h3>Career session briefing</h3>
                  <p className='northstarBodyCopy'>
                    When you start Firefly from here, it should coach you using your score, your blockers, and the active curriculum objective.
                  </p>
                  <div className='northstarPromptCard'>
                    <span>Suggested prompt</span>
                    <p>{fireflyCareerPrompt}</p>
                  </div>
                  <div className='northstarButtonRow'>
                    <button type='button' className='northstarPrimaryBtn' onClick={handleFireflyComingSoon}>
                      Start Firefly Session
                    </button>
                    <Link to='/firefly-reports' className='northstarSecondaryBtn'>
                      View Reports
                    </Link>
                  </div>
                </article>
              </aside>
            </section>

            <section className='northstarCareerTabStrip'>
              {CAREER_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type='button'
                  className={`northstarSubTabBtn ${activeCareerTab === tab.key ? 'is-active' : ''}`}
                  onClick={() => setActiveCareerTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </section>

            {activeCareerTab === 'checklist' ? (
              <section className='northstarPanelGrid'>
                <article className='northstarCard northstarCardMain'>
                  <div className='northstarCardHeader'>
                    <div>
                      <p className='northstarSectionLabel'>Readiness Checklist</p>
                      <h2>Close the practical gaps before you apply</h2>
                    </div>
                  </div>

                  <div className='northstarChecklistGrid'>
                    {CAREER_CHECKLIST.map((item) => (
                      <button
                        key={item.key}
                        type='button'
                        className={`northstarCheckPill ${careerForm.checklist[item.key] ? 'is-checked' : ''}`}
                        onClick={() => toggleCareerCheck(item.key)}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </article>

                <aside className='northstarSidebar'>
                  <article className='northstarCard'>
                    <p className='northstarSectionLabel'>Checklist summary</p>
                    <h3>{checklistScore}% complete</h3>
                    <p className='northstarBodyCopy'>
                      This is your practical career-readiness layer — not just ambition, but whether the essentials are actually in place.
                    </p>
                    <div className='northstarMiniList'>
                      <div>
                        <span>Completed</span>
                        <strong>{CAREER_CHECKLIST.length - missingCareerChecklist.length} of {CAREER_CHECKLIST.length}</strong>
                      </div>
                      <div>
                        <span>Still open</span>
                        <strong>{missingCareerChecklist[0] || 'Nothing pending'}</strong>
                      </div>
                    </div>
                  </article>

                  <article className='northstarCard northstarCardSoft'>
                    <p className='northstarSectionLabel'>Profile assets</p>
                    <h3>Evidence stack</h3>
                    <div className='northstarMiniList'>
                      <div>
                        <span>CV</span>
                        <strong>{careerForm.cvFileName || 'Not uploaded yet'}</strong>
                      </div>
                      <div>
                        <span>Portfolio</span>
                        <strong>{careerForm.portfolioUrl ? 'Added' : 'Missing'}</strong>
                      </div>
                      <div>
                        <span>LinkedIn</span>
                        <strong>{careerForm.linkedinUrl ? 'Added' : 'Missing'}</strong>
                      </div>
                      <div>
                        <span>GitHub / proof of work</span>
                        <strong>{careerForm.githubUrl ? 'Added' : 'Missing'}</strong>
                      </div>
                    </div>
                  </article>
                </aside>
              </section>
            ) : null}

            {activeCareerTab === 'jobs' ? (
              <section className='northstarJobsSection'>
                <div className='northstarCardHeader'>
                  <div>
                    <p className='northstarSectionLabel'>Opportunity Feed</p>
                    <h2>Career-matching jobs from the admin feed</h2>
                    <p className='northstarBodyCopy'>
                      These roles are pulled from the opportunities already saved and monitored by the admin team, then filtered here for student action.
                    </p>
                  </div>
                  <button type='button' className='northstarSecondaryBtn' onClick={loadJobs} disabled={jobsLoading}>
                    {jobsLoading ? <Spinner animation='border' size='sm' /> : <FontAwesomeIcon icon={faBolt} />}
                    <span>{jobsLoading ? 'Refreshing...' : 'Refresh Jobs'}</span>
                  </button>
                </div>

                <div className='northstarJobsFilterBar'>
                  <label className='northstarField'>
                    <span>Search</span>
                    <input
                      value={jobFilters.search}
                      onChange={(event) => setJobFilters((current) => ({ ...current, search: event.target.value }))}
                      placeholder='Software developer, design, data...'
                    />
                  </label>
                  <label className='northstarField'>
                    <span>Location</span>
                    <input
                      value={jobFilters.location}
                      onChange={(event) => setJobFilters((current) => ({ ...current, location: event.target.value }))}
                      placeholder='Nairobi, Remote...'
                    />
                  </label>
                  <label className='northstarField'>
                    <span>Job type</span>
                    <select
                      value={jobFilters.jobType}
                      onChange={(event) => setJobFilters((current) => ({ ...current, jobType: event.target.value }))}
                    >
                      {JOB_TYPE_OPTIONS.map((option) => (
                        <option key={option.value || 'all'} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                </div>

                {jobsError ? (
                  <div className='northstarStateCard northstarStateCardSoft'>
                    <h3>Jobs could not load</h3>
                    <p>{jobsError}</p>
                  </div>
                ) : jobsLoading ? (
                  <div className='northstarStateCard northstarStateCardSoft'>
                    <h3>Refreshing jobs...</h3>
                    <p>We are loading the latest saved opportunities for students.</p>
                  </div>
                ) : visibleJobs.length === 0 ? (
                  <div className='northstarStateCard northstarStateCardSoft'>
                    <h3>No matching jobs yet</h3>
                    <p>Try adjusting your search or location filters to surface more saved roles.</p>
                  </div>
                ) : (
                  <div className='northstarJobsGrid'>
                    {visibleJobs.map((job) => (
                      <article key={job.id} className='northstarJobCard'>
                        <div className='northstarJobTop'>
                          <div>
                            <h3>{job.jobTitle || 'Untitled role'}</h3>
                            <p>{job.companyName || 'Unknown company'}</p>
                          </div>
                          <span className='northstarJobBadge'>{job.jobType || 'Any type'}</span>
                        </div>

                        <div className='northstarJobMeta'>
                          <span><FontAwesomeIcon icon={faLocationDot} /> {job.location || 'Location not captured'}</span>
                          <span><FontAwesomeIcon icon={faUsers} /> {job.sourceDomain || job.postedVia || 'Google Jobs'}</span>
                          <span>Posted {formatShortDate(job.postedDateIso || job.postedDateText)}</span>
                        </div>

                        <p className='northstarJobSnippet'>
                          {getJobDescription(job).slice(0, 220) || 'No description was captured for this job yet.'}
                          {getJobDescription(job).length > 220 ? '...' : ''}
                        </p>

                        <div className='northstarJobFooter'>
                          <strong>{job.salary || 'Salary not captured'}</strong>
                          {job.sourceUrl ? (
                            <a href={job.sourceUrl} target='_blank' rel='noreferrer'>
                              Open listing <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                            </a>
                          ) : (
                            <span className='northstarMuted'>Source unavailable</span>
                          )}
                        </div>
                      </article>
                    ))}
                  </div>
                )}

                <div className='northstarFooterMeta'>
                  <span>Jobs available now: {jobs.length}</span>
                  <strong>Last sync: {formatDateTime(latestRun?.completedAtMs || latestRun?.updatedAtMs)}</strong>
                </div>
              </section>
            ) : null}

            {activeCareerTab === 'curriculum' ? (
              <section className='northstarCurriculumGrid'>
                <article className='northstarCard northstarCurriculumRail'>
                  <div className='northstarCardHeader'>
                    <div>
                      <p className='northstarSectionLabel'>Daily Curriculum</p>
                      <h2>20-minute classes with tasks and submissions</h2>
                    </div>
                  </div>

                  <div className='northstarCurriculumList'>
                    {CAREER_CURRICULUM.map((item) => {
                      const progress = careerForm.curriculumProgress[item.id] || DEFAULT_CURRICULUM_ENTRY

                      return (
                        <button
                          key={item.id}
                          type='button'
                          className={`northstarCurriculumItem ${selectedCurriculumId === item.id ? 'is-active' : ''}`}
                          onClick={() => setSelectedCurriculumId(item.id)}
                        >
                          <div>
                            <span>{item.dayLabel}</span>
                            <strong>{item.title}</strong>
                          </div>
                          <div className='northstarCurriculumFlags'>
                            {progress.completed ? <small className='is-complete'>Class done</small> : null}
                            {progress.submitted ? <small className='is-submitted'>Submitted</small> : null}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </article>

                <article className='northstarCard northstarCurriculumDetail'>
                  <div className='northstarCardHeader'>
                    <div>
                      <p className='northstarSectionLabel'>{selectedCurriculum.dayLabel}</p>
                      <h2>{selectedCurriculum.title}</h2>
                    </div>
                    <div className='northstarButtonRow'>
                      <button
                        type='button'
                        className='northstarSecondaryBtn'
                        onClick={() => toggleCurriculumFlag(selectedCurriculum.id, 'completed')}
                      >
                        {selectedCurriculumProgress.completed ? 'Mark as not done' : 'Mark class complete'}
                      </button>
                      <button
                        type='button'
                        className='northstarPrimaryBtn'
                        onClick={() => toggleCurriculumFlag(selectedCurriculum.id, 'submitted')}
                      >
                        {selectedCurriculumProgress.submitted ? 'Undo submission' : 'Submit to Firefly log'}
                      </button>
                    </div>
                  </div>

                  <div className='northstarCurriculumMeta'>
                    <div>
                      <span>Lesson format</span>
                      <strong>20-minute guided class</strong>
                    </div>
                    <div>
                      <span>Deliverable</span>
                      <strong>{selectedCurriculum.deliverable}</strong>
                    </div>
                  </div>

                  <p className='northstarBodyCopy'>{selectedCurriculum.lesson}</p>

                  <div className='northstarTaskList'>
                    {selectedCurriculum.tasks.map((task, index) => (
                      <article key={`${selectedCurriculum.id}-${index}`} className='northstarTaskItem'>
                        <span>{index + 1}</span>
                        <p>{task}</p>
                      </article>
                    ))}
                  </div>

                  <div className='northstarFieldGrid'>
                    <label className='northstarField northstarField--wide'>
                      <span>Submission link</span>
                      <input
                        value={selectedCurriculumProgress.evidenceLink}
                        onChange={(event) => handleCurriculumField(selectedCurriculum.id, 'evidenceLink', event.target.value)}
                        placeholder='Paste a Google Doc, portfolio update, GitHub repo, or file link'
                      />
                    </label>
                    <label className='northstarField northstarField--wide'>
                      <span>What did you submit?</span>
                      <textarea
                        rows='3'
                        value={selectedCurriculumProgress.evidenceNotes}
                        onChange={(event) => handleCurriculumField(selectedCurriculum.id, 'evidenceNotes', event.target.value)}
                        placeholder='Describe the task you completed and what evidence Firefly should review.'
                      />
                    </label>
                    <label className='northstarField northstarField--wide'>
                      <span>Reflection for Firefly</span>
                      <textarea
                        rows='4'
                        value={selectedCurriculumProgress.reflection}
                        onChange={(event) => handleCurriculumField(selectedCurriculum.id, 'reflection', event.target.value)}
                        placeholder='What was hard? What do you want Firefly to coach you on next?'
                      />
                    </label>
                  </div>

                  <div className='northstarPromptCard northstarPromptCardWide'>
                    <span>Firefly coaching prompt</span>
                    <p>{fireflyCareerPrompt}</p>
                  </div>

                  <div className='northstarButtonRow'>
                    <button type='button' className='northstarPrimaryBtn' onClick={handleFireflyComingSoon}>
                      Start Firefly Session
                    </button>
                    <Link to='/firefly-reports' className='northstarSecondaryBtn'>
                      View Reports
                    </Link>
                  </div>
                </article>
              </section>
            ) : null}
          </>
        ) : (
          <section className='northstarPanelGrid'>
            <article className='northstarCard northstarCardMain'>
              <div className='northstarCardHeader'>
                <div>
                  <p className='northstarSectionLabel'>Startup Hub</p>
                  <h2>Manage your startup</h2>
                </div>
                <button
                  type='button'
                  className='northstarPrimaryBtn'
                  onClick={() => saveHub('venture')}
                  disabled={savingTrack === 'venture'}
                >
                  {savingTrack === 'venture' ? <Spinner animation='border' size='sm' /> : null}
                  <span>{savingTrack === 'venture' ? 'Saving...' : 'Save Startup Hub'}</span>
                </button>
              </div>

              <div className='northstarFieldGrid'>
                <label className='northstarField'>
                  <span>Startup name</span>
                  <input name='startupName' value={ventureForm.startupName} onChange={handleVentureField} placeholder='Northstar Labs' />
                </label>
                <label className='northstarField'>
                  <span>Stage</span>
                  <select name='stage' value={ventureForm.stage} onChange={handleVentureField}>
                    <option>Idea</option>
                    <option>Prototype</option>
                    <option>Pilot</option>
                    <option>Revenue</option>
                    <option>Scaling</option>
                  </select>
                </label>
                <label className='northstarField northstarField--wide'>
                  <span>One-line concept</span>
                  <input name='oneLiner' value={ventureForm.oneLiner} onChange={handleVentureField} placeholder='A simple description of what you are building' />
                </label>
                <label className='northstarField'>
                  <span>Target customer</span>
                  <input name='targetCustomer' value={ventureForm.targetCustomer} onChange={handleVentureField} placeholder='Who is this for?' />
                </label>
                <label className='northstarField'>
                  <span>Revenue model</span>
                  <input name='revenueModel' value={ventureForm.revenueModel} onChange={handleVentureField} placeholder='Subscription, one-off, B2B...' />
                </label>
                <label className='northstarField'>
                  <span>Current traction</span>
                  <input name='currentTraction' value={ventureForm.currentTraction} onChange={handleVentureField} placeholder='Users, pilots, sales, interest...' />
                </label>
                <label className='northstarField'>
                  <span>Next milestone</span>
                  <input name='nextMilestone' value={ventureForm.nextMilestone} onChange={handleVentureField} placeholder='What comes next?' />
                </label>
                <label className='northstarField northstarField--wide'>
                  <span>Launch timeline</span>
                  <input name='launchTimeline' value={ventureForm.launchTimeline} onChange={handleVentureField} placeholder='Example: pilot in 3 weeks' />
                </label>
                <label className='northstarField northstarField--wide'>
                  <span>Weekly focus</span>
                  <textarea name='weeklyFocus' value={ventureForm.weeklyFocus} onChange={handleVentureField} rows='3' placeholder='What exactly are you trying to move forward this week?' />
                </label>
                <label className='northstarField northstarField--wide'>
                  <span>Venture notes</span>
                  <textarea name='notes' value={ventureForm.notes} onChange={handleVentureField} rows='4' placeholder='Customer feedback, product notes, launch blockers...' />
                </label>
              </div>

              <div className='northstarChecklistWrap'>
                <div className='northstarSubhead'>
                  <h3>Launch checklist</h3>
                  <p>Use this to keep your venture grounded in validation and execution.</p>
                </div>
                <div className='northstarChecklistGrid'>
                  {VENTURE_CHECKLIST.map((item) => (
                    <button
                      key={item.key}
                      type='button'
                      className={`northstarCheckPill ${ventureForm.checklist[item.key] ? 'is-checked' : ''}`}
                      onClick={() => toggleVentureCheck(item.key)}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            </article>

            <aside className='northstarSidebar'>
              <article className='northstarCard'>
                <p className='northstarSectionLabel'>Launch signal</p>
                <h3>{ventureScore}% launch-ready</h3>
                <p className='northstarBodyCopy'>{ventureInsight}</p>
                <div className='northstarMiniList'>
                  <div>
                    <span>Stage</span>
                    <strong>{ventureForm.stage || 'Not set yet'}</strong>
                  </div>
                  <div>
                    <span>Milestone</span>
                    <strong>{ventureForm.nextMilestone || 'Not set yet'}</strong>
                  </div>
                  <div>
                    <span>Focus this week</span>
                    <strong>{ventureForm.weeklyFocus || 'Not set yet'}</strong>
                  </div>
                </div>
              </article>

              <article className='northstarCard northstarCardSoft'>
                <p className='northstarSectionLabel'>Execution board</p>
                <h3>Keep the venture honest</h3>
                <p className='northstarBodyCopy'>
                  Stay close to traction, feedback, and the next sharp milestone. The point is not just ideation. It is movement.
                </p>
                <div className='northstarMiniList'>
                  <div>
                    <span>Startup</span>
                    <strong>{ventureForm.startupName || 'Unnamed venture'}</strong>
                  </div>
                  <div>
                    <span>Target customer</span>
                    <strong>{ventureForm.targetCustomer || 'Not set yet'}</strong>
                  </div>
                  <div>
                    <span>Timeline</span>
                    <strong>{ventureForm.launchTimeline || 'Not set yet'}</strong>
                  </div>
                </div>
              </article>
            </aside>
          </section>
        )}
      </div>
    </div>
  )
}

export default NorthstarHubComp
